/**
 * Household match detection — whole-roster consensus on a recipe.
 * Requires 2+ active members (not couples-only) and respects kid/adult dietary eligibility.
 */

/** Minimum roster size — distinguishes Suppertide from couples-only swipe apps. */
export const MIN_ROSTER_SIZE = 2;

/** Composite score threshold when not every eligible member has swiped yet. Base 1 + +3 per like. */
export const HIGH_SCORE_THRESHOLD = 7;

export function getActiveMembers(members) {
  return (members || []).filter(m => !m.archived_at);
}

/** Members who can scout this recipe given dietary restrictions. */
export function getEligibleMembers(members, recipe) {
  const active = getActiveMembers(members);
  if (!recipe) return active;
  return active.filter(m => {
    if (!m.allergies?.length) return true;
    return m.allergies.every(req => recipe.tags?.includes(req));
  });
}

export function groupSwipesByMember(rawSwipes) {
  const grouped = {};
  for (const s of rawSwipes || []) {
    if (!grouped[s.member_id]) grouped[s.member_id] = {};
    grouped[s.member_id][s.recipe_id] = s.liked;
  }
  return grouped;
}

function getRecipeScore(recipeId, members, swipesGrouped) {
  let score = 1;
  for (const m of members) {
    const liked = swipesGrouped[m.id]?.[recipeId];
    if (liked === true) score += 3;
    else if (liked === false) score -= 2;
  }
  return score;
}

export function formatMatchMessage(recipe) {
  const emoji = recipe?.emoji ? `${recipe.emoji} ` : '';
  const name = recipe?.name || 'this meal';
  return `It's a match! Whole family wants ${emoji}${name}`;
}

/**
 * Detect whether the household reached consensus on a recipe.
 * @returns {null | { recipe, members, reason: 'unanimous' | 'high_score', hasKidAndAdult: boolean }}
 */
export function detectHouseholdMatch({ members, recipes, rawSwipes, recipeId }) {
  const recipe = recipes.find(r => r.id === recipeId);
  if (!recipe) return null;

  const activeMembers = getActiveMembers(members);
  if (activeMembers.length < MIN_ROSTER_SIZE) return null;

  const eligible = getEligibleMembers(activeMembers, recipe);
  if (eligible.length < MIN_ROSTER_SIZE) return null;

  const recipeSwipes = (rawSwipes || []).filter(s => s.recipe_id === recipeId);
  if (!recipeSwipes.length) return null;

  if (recipeSwipes.some(s => !s.liked)) return null;

  const likedIds = new Set(recipeSwipes.map(s => s.member_id));
  const swipesGrouped = groupSwipesByMember(rawSwipes);
  const hasKidAndAdult = eligible.some(m => m.type === 'kid') && eligible.some(m => m.type === 'adult');

  const unanimous = eligible.every(m => likedIds.has(m.id));
  if (unanimous) {
    return { recipe, members: eligible, reason: 'unanimous', hasKidAndAdult };
  }

  const score = getRecipeScore(recipeId, activeMembers, swipesGrouped);
  if (likedIds.size >= MIN_ROSTER_SIZE && score >= HIGH_SCORE_THRESHOLD) {
    const likers = eligible.filter(m => likedIds.has(m.id));
    return { recipe, members: likers, reason: 'high_score', hasKidAndAdult };
  }

  return null;
}

/** All recipes with an active household match (for activity feed). */
export function findHouseholdMatches({ members, recipes, rawSwipes, maxAgeMs }) {
  const likedRecipeIds = new Set(
    (rawSwipes || []).filter(s => s.liked).map(s => s.recipe_id),
  );
  const cutoff = maxAgeMs ? Date.now() - maxAgeMs : 0;
  const matches = [];

  for (const recipeId of likedRecipeIds) {
    const match = detectHouseholdMatch({ members, recipes, rawSwipes, recipeId });
    if (!match) continue;

    const recipeSwipes = (rawSwipes || []).filter(s => s.recipe_id === recipeId && s.liked);
    const latest = [...recipeSwipes].sort((a, b) => (b.swiped_at || '').localeCompare(a.swiped_at || ''))[0];
    if (maxAgeMs && (!latest?.swiped_at || new Date(latest.swiped_at).getTime() < cutoff)) continue;

    matches.push({ ...match, timestamp: latest?.swiped_at });
  }

  return matches;
}

const SHOWN_MATCHES_KEY = 'dinner-league-shown-matches';

export function getShownMatchIds() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SHOWN_MATCHES_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

export function markMatchShown(recipeId) {
  const shown = getShownMatchIds();
  shown.add(recipeId);
  sessionStorage.setItem(SHOWN_MATCHES_KEY, JSON.stringify([...shown]));
}
